import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import ActiveConnectorCard from '../components/ActiveConnectorCard'
import ConnectorCatalogCard from '../components/ConnectorCatalogCard'
import Icon from '../components/Icon'
import styles from './Connectors.module.css'
import { fetchActiveConnectors, fetchConnectorCatalog, addConnectorFromCatalog } from '../api/connectors'
import { getTokenPayload } from '../api/security'

// ---------------------------------------------------------------------------
// Derive the left-bar colour and card status from the API response
// ---------------------------------------------------------------------------
function resolveStatus(connector) {
  // The API sends either a `status` field ("Active" / "Error") or an `error`
  // boolean-string. Treat either as the source of truth.
  const hasError =
    connector.error === true ||
    connector.error === 'true' ||
    connector.status?.toLowerCase() === 'error'
  return hasError ? 'error' : 'active'
}

function resolveBarColor(status) {
  return status === 'error' ? '--color-semantic-red' : '--color-semantic-green'
}

function formatLastExecution(isoString) {
  if (!isoString) return ''
  const date = new Date(isoString)
  if (isNaN(date.getTime())) return isoString  // pass through if not a valid date
  return 'Last Executed: ' + date.toLocaleString('en-US', {
    month:  'short',
    day:    'numeric',
    year:   'numeric',
    hour:   'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

// ---------------------------------------------------------------------------
// Connectors page — superadmin_right only (Sidebar already hides the link for
// everyone else; this is the matching guard for a direct /connect URL hit,
// same redirect-away pattern admin.jsx uses for /admin). The fetch effects
// are gated too, not just the render — they live in this same component
// rather than a child only mounted after the guard, unlike admin.jsx's tabs.
// ---------------------------------------------------------------------------
export default function Connectors() {
  const navigate = useNavigate()
  const isSuperAdmin = Boolean(getTokenPayload()?.rights?.superadmin_right)

  const [connectors,  setConnectors]  = useState([])
  const [catalog,     setCatalog]     = useState([])
  const [loading,     setLoading]     = useState(true)
  const [catalogLoad, setCatalogLoad] = useState(true)
  const [error,       setError]       = useState(null)
  const [connectingId, setConnectingId] = useState(null)

  useEffect(() => {
    if (!isSuperAdmin) navigate('/dashboard', { replace: true })
  }, [isSuperAdmin, navigate])

  useEffect(() => {
    if (!isSuperAdmin) return
    fetchActiveConnectors()
      .then(data => setConnectors(data.connectors ?? []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [isSuperAdmin])

  useEffect(() => {
    if (!isSuperAdmin) return
    fetchConnectorCatalog()
      .then(data => setCatalog(data.connectors ?? []))
      .catch(() => {/* catalog errors are non-fatal, fail silently */})
      .finally(() => setCatalogLoad(false))
  }, [isSuperAdmin])

  if (!isSuperAdmin) return null

  async function handleConnect(catalogId) {
    if (!catalogId || connectingId) return
    setConnectingId(catalogId)
    try {
      await addConnectorFromCatalog(catalogId)
      const [activeData, catalogData] = await Promise.all([
        fetchActiveConnectors(),
        fetchConnectorCatalog(),
      ])
      setConnectors(activeData.connectors ?? [])
      setCatalog(catalogData.connectors ?? [])
    } catch (e) {
      setError(e.message)
    } finally {
      setConnectingId(null)
    }
  }


  return (
    <div className={styles.page}>

      {/* ── Page header ─────────────────────────────── */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Connectors</h1>
          <p className={styles.pageSub}>Manage and monitor your active data-source integrations.</p>
        </div>
      </div>

      {/* ── Error banner ────────────────────────────── */}
      {error && (
        <div className={styles.errorBanner}>
          <Icon name="info" className={styles.errorBannerIcon} />
          {error}
        </div>
      )}

      {/* ── Connector list ──────────────────────────── */}
      <div className={styles.connectorList}>
        {loading
          ? Array(3).fill(0).map((_, i) => (
              <div key={i} className={styles.skeleton} />
            ))
          : connectors.map(connector => {
              const status = resolveStatus(connector)
              // Vite serves public/ at root; strip any leading path, keep just the filename
              const iconUrl = connector.icon
                ? '/' + connector.icon.replace(/^\/public\/images\//, '').replace(/^\/images\//, '').replace(/^\//, '')
                : ''
              return (
                <ActiveConnectorCard
                  key={connector.name}
                  connectorId={connector.connector_id}
                  icon={iconUrl}
                  name={connector.name}
                  description={connector.description}
                  lastSynced={formatLastExecution(connector.last_execution)}
                  status={status}
                  barColor={resolveBarColor(status)}
                  actions={(connector.actions ?? []).map(a => ({
                    action_id:    a.action_id,
                    label:        a.action,
                    enabled:      a.status,
                    outcome:      a.outcome ?? null,
                    errorMessage: a.error_message ?? '',
                  }))}
                />
              )
            })
        }
      </div>

      {/* ── Connector catalog ───────────────────────── */}
      <div className={styles.catalogSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Available Connectors</h2>
          <p className={styles.sectionSub}>Click + Connect to set up a new integration.</p>
        </div>
        <div className={styles.catalogGrid}>
          {catalogLoad
            ? Array(6).fill(0).map((_, i) => (
                <div key={i} className={styles.catalogSkeleton} />
              ))
            : catalog.map(item => (
                <ConnectorCatalogCard
                  key={item.connector_name}
                  icon={item.icon}
                  name={item.connector_name}
                  description={item.description}
                  category={item.catagory}
                  connecting={connectingId === item.catalog_id}
                  onConnect={() => handleConnect(item.catalog_id)}
                />
              ))
          }
        </div>
      </div>

    </div>
  )
}
